/* eslint-disable max-len */
import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import API from '../../api/endpoints';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { sendRequest } from '../../api/shootAPI';
import RowActionMenu from '../RowActionMenu';
import ConfirmDialog from '../ConfirmDialog';

function UserEditColumn({
  member, deactivateMember, editMember, style, setupAlert, onMemberUpdate,
}) {
  const { activeWorkspace, user } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);

  const [confirm, setConfirm] = useState(null); // { title, description, onConfirm }
  const [confirming, setConfirming] = useState(false);

  const resendInvitation = async () => {
    const response = await sendRequest({
      method: 'post',
      url: API.WORKSPACE.resendInvitation(activeWorkspace.id, member.userId),
      data: {
        owner: user,
      },
    });
    if (response.success) {
      displayNotification({
        message: 'Resend invitation successfully.',
        severity: 'success',
      });
    } else {
      displayNotification({
        message: 'There was an issue resending invitation. Please try again',
        severity: 'error',
      });
    }
  };

  const toggleIdleTimeTracking = async () => {
    const newState = !member.idleTimeTrackingEnabled;
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.memberToggleIdleTimeTracking(activeWorkspace.id, member.id),
      data: {
        enabled: newState,
      },
    });
    if (response.success) {
      displayNotification({
        message: `Idle time tracking ${newState ? 'enabled' : 'disabled'} successfully.`,
        severity: 'success',
      });
      if (onMemberUpdate) {
        onMemberUpdate({ ...member, idleTimeTrackingEnabled: newState });
      }
    } else {
      displayNotification({
        message: 'There was an issue updating idle time tracking. Please try again',
        severity: 'error',
      });
    }
  };

  const toggleManualTracker = async () => {
    const newState = !member.manualTrackerEnabled;
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.memberToggleManualTracker(activeWorkspace.id, member.id),
      data: {
        enabled: newState,
      },
    });
    if (response.success) {
      displayNotification({
        message: `Manual tracker ${newState ? 'enabled' : 'disabled'} successfully.`,
        severity: 'success',
      });
      if (onMemberUpdate) {
        onMemberUpdate({ ...member, manualTrackerEnabled: newState });
      }
    } else {
      displayNotification({
        message: 'There was an issue updating manual tracker. Please try again',
        severity: 'error',
      });
    }
  };

  const memberName = `${member.user?.firstName || ''} ${member.user?.lastName || ''}`.trim()
    || member.user?.email;

  const handleToggleIdleTimeTracking = () => {
    const newState = !member.idleTimeTrackingEnabled;
    setConfirm({
      title: `${newState ? 'Enable' : 'Disable'} Idle Time Tracking`,
      description: `Are you sure you want to ${newState ? 'enable' : 'disable'} idle time tracking for ${memberName}? ${newState ? 'The system will track and prompt for idle periods.' : 'Idle periods will not be tracked.'}`,
      onConfirm: toggleIdleTimeTracking,
    });
  };

  const handleToggleManualTracker = () => {
    const newState = !member.manualTrackerEnabled;
    setConfirm({
      title: `${newState ? 'Enable' : 'Disable'} Manual Tracker`,
      description: `Are you sure you want to ${newState ? 'enable' : 'disable'} manual tracker for ${memberName}? ${newState ? 'The member will be able to manually enter time.' : 'Manual time entry will be disabled.'}`,
      onConfirm: toggleManualTracker,
    });
  };

  const runConfirm = async () => {
    if (!confirm?.onConfirm) return;
    setConfirming(true);
    await confirm.onConfirm();
    setConfirming(false);
    setConfirm(null);
  };

  if (!member.status) return null;

  const items = [
    ...(!member.haveJoined
      ? [{ label: 'Resend Invitation', onClick: resendInvitation }]
      : []),
    { label: 'Edit User', onClick: () => editMember(member) },
    ...(setupAlert ? [{ label: 'Setup Alerts', onClick: () => setupAlert(member) }] : []),
    {
      label: `${member.idleTimeTrackingEnabled ? 'Disable' : 'Enable'} Idle Time Tracking`,
      onClick: handleToggleIdleTimeTracking,
    },
    {
      label: `${member.manualTrackerEnabled ? 'Disable' : 'Enable'} Manual Tracker`,
      onClick: handleToggleManualTracker,
    },
    { label: 'Deactivate User', onClick: () => deactivateMember(member), destructive: true },
  ];

  return (
    <div style={style}>
      <RowActionMenu
        orientation="vertical"
        iconSize={16}
        ariaLabel="Open member actions"
        triggerClassName="inline-flex items-center justify-center p-1 rounded hover:bg-surface-1 text-ink-500 transition bg-transparent border-0"
        items={items}
      />
      {confirm ? (
        <ConfirmDialog
          tone="brand"
          title={confirm.title}
          description={confirm.description}
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          confirming={confirming}
          onConfirm={runConfirm}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </div>
  );
}

export default UserEditColumn;

UserEditColumn.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number.isRequired,
    userId: PropTypes.number.isRequired,
    status: PropTypes.bool.isRequired,
    accountType: PropTypes.string.isRequired,
    haveJoined: PropTypes.bool.isRequired,
    idleTimeTrackingEnabled: PropTypes.bool,
    manualTrackerEnabled: PropTypes.bool,
    user: PropTypes.shape({
      firstName: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.oneOf([null]),
      ]),
      lastName: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.oneOf([null]),
      ]),
      email: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  deactivateMember: PropTypes.func.isRequired,
  editMember: PropTypes.func.isRequired,
  style: PropTypes.shape({}),
  setupAlert: PropTypes.func,
  onMemberUpdate: PropTypes.func,
};

UserEditColumn.defaultProps = {
  style: undefined,
  setupAlert: null,
  onMemberUpdate: null,
};
